import React, { useMemo } from 'react';
import { intlShape } from '../../util/reactIntl';
import { convertNumberToMoney, formatMoney } from '../../util/currency';
import { LINE_ITEM_SEATS, LINE_ITEM_SESSION, propTypes } from '../../util/types';
import { getSessionDatesFromLineItemCode } from '../../util/sessions';
import { getDayOfWeekStrings } from '../../util/dates';
import { bool, string } from 'prop-types';
import classNames from 'classnames';

import css from './OrderBreakdown.module.css';

const LineItemSessions = props => {
  const {
    className,
    transaction,
    timeZone,
    intl,
    isEstimated,
    bookings: propBookings = [],
    isProvider,
    hidePrice,
    hideTitle,
  } = props;

  const sessionLineItems = transaction.attributes.lineItems.filter(
    item => item.code.includes(LINE_ITEM_SESSION) && !item.reversal
  );

  const weekdays = useMemo(() => getDayOfWeekStrings(), []);

  if (sessionLineItems.length === 0) return null;

  // Get seats from line items first, fallback to protectedData
  const seatsLineItem = transaction.attributes.lineItems.find(
    item => item.code === LINE_ITEM_SEATS && !item.reversal
  );
  
  let seats = seatsLineItem?.quantity || 0;
  
  // Fallback: Get seats from protectedData if not in line items
  if (seats === 0) {
    const txProtectedData = transaction?.attributes?.protectedData || {};
    const txAdults = txProtectedData.adults || 0;
    const txChildren = txProtectedData.children || 0;
    seats = txProtectedData.seats || (txAdults > 0 || txChildren > 0 ? txAdults + txChildren : 0);
    
  }
  
  // For customers: add customer fee to session prices for display
  // Customers see inclusive prices (base + fee)
  // Providers see base prices only
  const isCustomer = !isProvider;
  const customerCommissionLineItem = isCustomer
    ? transaction.attributes.lineItems.find(
        item => item.code === 'line-item/customer-commission' && !item.reversal
      )
    : null;
  
  const customerFeePercentage = customerCommissionLineItem?.percentage || 0;

  if (isEstimated) {
    const { total: sessionsTotal, currency, quantity } = sessionLineItems.reduce(
      (sessionTotal, lineItem) => {
        const { lineTotal, quantity } = lineItem;

        sessionTotal.total += lineTotal.amount;
        sessionTotal.currency = lineTotal.currency;
        sessionTotal.quantity = quantity;

        return sessionTotal;
      },
      { total: 0, currency: null, quantity: 1 }
    );
    
    // DEBUG: Removed verbose estimated total logs

    // For customers: show inclusive price (base + fee)
    // For providers: show base price only
    const displayTotal = isCustomer && customerFeePercentage > 0
      ? sessionsTotal * (1 + customerFeePercentage / 100)
      : sessionsTotal;
    
    const total = formatMoney(intl, convertNumberToMoney(displayTotal, currency), true);

    return (
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          {intl.formatMessage(
            { id: 'BookingBreakdown.sessionsCount' },
            { sessionsCount: sessionLineItems.length / quantity, peopleCount: seats }
          )}
        </span>
        <span className={css.itemValue}>{total}</span>
      </div>
    );
  }

  // Get transaction state to determine if we should exclude cancelled bookings
  const lastTransition = transaction?.attributes?.lastTransition;
  const isCancelledTransaction = lastTransition && (
    // Manual cancellations/declines
    lastTransition === 'transition/decline' ||
    lastTransition === 'transition/expire' ||
    lastTransition === 'transition/expire-payment' ||
    lastTransition === 'transition/cancel-pending-payment' ||
    lastTransition === 'transition/cancel-preauthorized' ||
    lastTransition === 'transition/cancel-preauthorized-by-customer' ||
    lastTransition === 'transition/cancel-accepted' ||
    lastTransition === 'transition/cancel-accepted-by-customer' ||
    lastTransition === 'transition/cancel-accepted-by-provider' ||
    // Automatic Stripe refund transitions (happen after manual transitions)
    lastTransition === 'transition/declined-stripe' ||
    lastTransition === 'transition/cancelled-stripe' ||
    lastTransition === 'transition/payment-expired-stripe' ||
    // Operator-initiated refunds
    lastTransition === 'transition/auto-refund' ||
    lastTransition === 'transition/auto-refund-no-payment' ||
    lastTransition === 'transition/manual-refund' ||
    lastTransition === 'transition/full-refund-auto'
  );

  // Filter bookings for display:
  // - For current transaction: Keep ALL bookings (including cancelled) to show customer details in popup
  // - For other transactions: Only show non-cancelled bookings (for accurate people counter)
  const currentTxId = transaction?.id?.uuid;
  const relevantBookings = propBookings?.filter(booking => {
    // If booking belongs to current transaction, always keep it (even if cancelled)
    // This allows provider to see customer details in hover popup
    if (booking.transactionId === currentTxId) {
      return true;
    }
    // For other transactions, only show non-cancelled bookings
    return booking.status !== 'CANCELLED';
  }) || [];
  
  // If no relevant bookings, use transaction protectedData for seats (ONLY if not cancelled)
  // For cancelled transactions, seats should be 0
  const protectedData = transaction?.attributes?.protectedData || {};
  const adultsFromProtectedData = protectedData.adults || 0;
  const childrenFromProtectedData = protectedData.children || 0;
  const seatsFromProtectedData = isCancelledTransaction 
    ? 0 
    : (protectedData.seats || (adultsFromProtectedData + childrenFromProtectedData));
  
  // DEBUG: ALWAYS log in provider view to understand seat count issue
  if (isProvider) {
    console.log('[LineItemSessions] Provider view - seat count debug:', {
      txId: currentTxId,
      lastTransition,
      isCancelledTransaction,
      propBookingsCount: propBookings?.length || 0,
      relevantBookingsCount: relevantBookings.length,
      relevantBookings: relevantBookings.map(b => ({
        txId: b.transactionId,
        status: b.status,
        seats: b.seats,
        customer: b.customer
      })),
      adultsFromProtectedData,
      childrenFromProtectedData,
      seatsFromProtectedData
    });
  }

  const from = intl.formatMessage({ id: 'General.from' });
  const to = intl.formatMessage({ id: 'General.to' });

  return (
    <>
      {!hideTitle && (
        <div className={css.itemLabel}>{intl.formatMessage({ id: 'General.time' })}</div>
      )}
      <ul className={classNames(css.list, className)}>
        {sessionLineItems
          .sort((l1, l2) => {
            const { startDate: firstStartDate } = getSessionDatesFromLineItemCode(
              l1.code,
              timeZone
            );
            const { startDate: secondStartDate } = getSessionDatesFromLineItemCode(
              l2.code,
              timeZone
            );

            return firstStartDate.toDate() - secondStartDate.toDate();
          })
          .map(lineItem => {
            const { code, lineTotal } = lineItem;

            const {
              startDate,
              endDate,
            } = getSessionDatesFromLineItemCode(code, timeZone);

            const dayOfWeek =
              weekdays[startDate.isoWeekday() - 1]?.localizedLong || startDate.format('dddd');

            const startDoWAndDay = `${dayOfWeek} ${startDate.format('DD')}`;

            const startTime = startDate.format('HH:mm');
            const endTime = endDate.format('HH:mm');

            const endTimeFinal = endTime === '00:00' ? '24:00' : endTime;

            // DEBUG logging removed

            // For customers: show inclusive price (base + fee)
            // For providers: show base price only
            const displayPrice = isCustomer && customerFeePercentage > 0
              ? convertNumberToMoney(
                  lineTotal.amount * (1 + customerFeePercentage / 100),
                  lineTotal.currency
                )
              : lineTotal;
            
            const total = formatMoney(intl, displayPrice, true);

            const sessionTime = `${from} ${startTime} ${to} ${endTimeFinal}`.toLocaleLowerCase();

            return (
              <li className={classNames(css.lineItem, css.listItem)} key={code}>
                <span className={classNames(css.itemLabel, css.itemLabelSession)}>
                  <span>{`${startDoWAndDay} ${sessionTime}`}</span>
                </span>
                {!hidePrice && (
                  <span className={css.itemValue}>
                    {total}
                  </span>
                )}
              </li>
            );
          })}
      </ul>
    </>
  );
};

LineItemSessions.propTypes = {
  transaction: propTypes.transaction.isRequired,
  timeZone: string.isRequired,
  isEstimated: bool,
  intl: intlShape.isRequired,
};

export default LineItemSessions;
